import { BooleanOperator, QueryBuilderSubquery } from '@ardoq/api-types';
import { t, type I18nLocale } from '@ardoq/i18n';

export enum SurveySectionId {
  GENERAL_INFORMATION = 'GENERAL_INFORMATION',
  DEFINE_THE_DATA = 'DEFINE_THE_DATA',
  QUESTIONS = 'QUESTIONS',
  RESULTS_DISPLAY = 'RESULTS_DISPLAY',
  CHANGE_APPROVAL = 'CHANGE_APPROVAL',
}

export enum SurveyWarningName {
  // Change approval
  CHANGE_APPROVAL_EMPTY_USER_GROUP = 'CHANGE_APPROVAL_EMPTY_USER_GROUP',
  // Result Filtering
  NO_ACCESS_TO_SELECTED_RESULT_FILTERING_WORKSPACE = 'NO_ACCESS_TO_SELECTED_RESULT_FILTERING_WORKSPACE',
  QUESTION_WARNINGS_EXIST = 'QUESTION_WARNINGS_EXIST',
}

export enum SurveyErrorName {
  // General
  NAME_INVALID = 'NAME_INVALID',
  CONTACT_EMAIL_INVALID = 'CONTACT_EMAIL_INVALID',
  // Hidden fields
  HIDDEN_FIELDS = 'HIDDEN_FIELDS',
  // Workspace
  WORKSPACE_INVALID = 'WORKSPACE_INVALID',
  COMPONENT_TYPE_INVALID = 'COMPONENT_TYPE_INVALID',
  REQUIRED_NEW_COMPONENT_PARENT_IS_MISSING = 'REQUIRED_NEW_COMPONENT_PARENT_IS_MISSING',
  SELECTED_NEW_COMPONENT_PARENT_DOES_NOT_EXIST = 'SELECTED_NEW_COMPONENT_PARENT_DOES_NOT_EXIST',
  NO_ADMIN_ACCESS_TO_SURVEY_WORKSPACE = 'NO_ADMIN_ACCESS_TO_SURVEY_WORKSPACE',
  // Result filtering
  SELECTED_RESULT_FILTERING_COMPONENT_TYPE_INVALID = 'SELECTED_RESULT_FILTERING_COMPONENT_TYPE_INVALID',
  SELECTED_RESULT_FILTERING_REFERENCE_TYPE_INVALID = 'SELECTED_RESULT_FILTERING_REFERENCE_TYPE_INVALID',
  MISSING_RESULT_FILTERING_FIELDS = 'MISSING_RESULT_FILTERING_FIELDS',
  // Questions
  QUESTION_ERRORS_EXIST = 'QUESTION_ERRORS_EXIST',
  MISSING_NAME_QUESTION = 'MISSING_NAME_QUESTION',
  // Change approval
  CHANGE_APPROVAL_COMPATIBILITY = 'CHANGE_APPROVAL_COMPATIBILITY',
  CHANGE_APPROVAL_INVALID_ADVANCED_AUDIENCE = 'CHANGE_APPROVAL_INVALID_ADVANCED_AUDIENCE',
  CHANGE_APPROVAL_FALLBACK_USER = 'CHANGE_APPROVAL_FALLBACK_USER',
}

const surveyErrorNameDefaults: Record<SurveyErrorName, string> = {
  [SurveyErrorName.NAME_INVALID]: 'The survey name is invalid',
  [SurveyErrorName.CONTACT_EMAIL_INVALID]:
    'The contact email or URL is invalid',
  [SurveyErrorName.HIDDEN_FIELDS]: 'Some of the hidden fields are invalid',
  [SurveyErrorName.WORKSPACE_INVALID]:
    'The workspace has not been selected, or no longer exists',
  [SurveyErrorName.COMPONENT_TYPE_INVALID]:
    'The component type is invalid or does not exist',
  [SurveyErrorName.REQUIRED_NEW_COMPONENT_PARENT_IS_MISSING]:
    'No parent was selected for new components',
  [SurveyErrorName.SELECTED_NEW_COMPONENT_PARENT_DOES_NOT_EXIST]:
    'The parent for new components does not exist',
  [SurveyErrorName.NO_ADMIN_ACCESS_TO_SURVEY_WORKSPACE]:
    'You do not have "Administrator" access on the selected workspace',
  [SurveyErrorName.SELECTED_RESULT_FILTERING_COMPONENT_TYPE_INVALID]:
    'The selected result filtering component type is invalid',
  [SurveyErrorName.SELECTED_RESULT_FILTERING_REFERENCE_TYPE_INVALID]:
    'The selected result filtering reference type is invalid',
  [SurveyErrorName.MISSING_RESULT_FILTERING_FIELDS]:
    'The survey has missing result filtering fields',
  [SurveyErrorName.QUESTION_ERRORS_EXIST]:
    'There are errors in the questions section',
  [SurveyErrorName.MISSING_NAME_QUESTION]:
    'The survey is missing the name question',
  [SurveyErrorName.CHANGE_APPROVAL_COMPATIBILITY]:
    'Your survey does not support Response Approvals. Please remove the tag question, or turn off this feature.',
  [SurveyErrorName.CHANGE_APPROVAL_FALLBACK_USER]:
    'No backup approver has been assigned',
  [SurveyErrorName.CHANGE_APPROVAL_INVALID_ADVANCED_AUDIENCE]:
    'The advanced audience is invalid — one of the selected component or reference types has been deleted. Edit the relationship or select a different audience type to continue.',
};

const surveyWarningNameDefaults: Record<SurveyWarningName, string> = {
  [SurveyWarningName.CHANGE_APPROVAL_EMPTY_USER_GROUP]:
    'The approval audience contains an empty user group. This will effect who receives approval requests.',
  [SurveyWarningName.NO_ACCESS_TO_SELECTED_RESULT_FILTERING_WORKSPACE]:
    'You do not have "Read" access on the selected result filtering workspace',
  [SurveyWarningName.QUESTION_WARNINGS_EXIST]:
    'There are warnings in the questions section',
};

export const getSurveyErrorNameMessage = (
  code: SurveyErrorName,
  i18nLocale: I18nLocale
): string =>
  t(
    i18nLocale,
    `surveyAdmin:validationMessage.error.${code}`,
    surveyErrorNameDefaults[code]
  );

export const getSurveyWarningNameMessage = (
  code: SurveyWarningName,
  i18nLocale: I18nLocale
): string =>
  t(
    i18nLocale,
    `surveyAdmin:validationMessage.warning.${code}`,
    surveyWarningNameDefaults[code]
  );

export const REQUIRED_FIELD_MESSAGE = 'This field is required';

export const referenceDirectionOptions = [
  { label: 'Incoming', value: false, description: 'To the Survey component' },
  {
    label: 'Outgoing',
    value: true,
    description: 'From the Survey component',
  },
];

export const getReferenceDirectionOptions = (i18nLocale: I18nLocale) => [
  {
    label: t(i18nLocale, 'surveyAdmin:referenceDirection.incoming', 'Incoming'),
    value: false,
    description: t(
      i18nLocale,
      'surveyAdmin:referenceDirection.incomingDescription',
      'To the Survey component'
    ),
  },
  {
    label: t(i18nLocale, 'surveyAdmin:referenceDirection.outgoing', 'Outgoing'),
    value: true,
    description: t(
      i18nLocale,
      'surveyAdmin:referenceDirection.outgoingDescription',
      'From the Survey component'
    ),
  },
];

export const DEFAULT_ADVANCED_SEARCH_LIMITER: QueryBuilderSubquery = {
  condition: BooleanOperator.AND,
  rules: [],
};
